import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { CreditProvider } from './CreditContext';
import { SubscriptionProvider } from './SubscriptionContext';
import { FlashSale } from '@trialpod/ui';
import axios from 'axios';
import { GetCookie } from '../utils/cookies';
import { Auth } from '../utils/auth';

function GlobalProviderContent({
  children,
  subscription = {},
}) {
  const [aiproUser, setAiproUser] = useState({
    plan_name: '',
    subscription_start: { date: '' },
    subscription_end: { date: '' },
    currency: '',
    user_ppg: ''
  });
  const [loading, setLoading] = useState(true);
  const auth = Auth();

  const getActiveSubscription = useCallback((subscriptions) => {
    if (!Array.isArray(subscriptions) || subscriptions.length === 0) {
      return null;
    }
    
    const activeSubscription = subscriptions.find(sub => sub.status === 'active');
    if (activeSubscription) {
      return activeSubscription;
    }
    
    return subscriptions[0];
  }, []);

  useEffect(() => {
    let isMounted = true;
    const tk = GetCookie('access');

    const fetchSubscriptions = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/get-subscription`,
          { tk: tk || '' },
          { headers: { 'content-type': 'application/x-www-form-urlencoded' } }
        );
        
        if (isMounted && response.data?.success && Array.isArray(response.data.data)) {
          const subscriptions = response.data.data;
          // console.log('get-subscription:', subscriptions);
          
          // Get the active subscription or fallback to first one
          const activeSubscription = getActiveSubscription(subscriptions);
          
          if (activeSubscription) {
            setAiproUser({
              plan_name: activeSubscription.plan_name || '',
              subscription_start: { date: activeSubscription.start_date || '' },
              subscription_end: { date: activeSubscription.trial_end || '' },
              currency: activeSubscription.currency || '',
              is_trial_end: activeSubscription.is_trial_end || '',
              user_ppg: activeSubscription.user_ppg || ''
            });
          }
        }
      } catch (e) {
        if (isMounted) {
          console.error('get-subscription error:', e);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (!tk) {
      return;
    }

    fetchSubscriptions();
    
    return () => {
      isMounted = false;
    };
  }, [getActiveSubscription]);

  const contextValue = useMemo(() => ({ aiproUser, loading }), [aiproUser, loading]);

  const shouldShowFlashSale = useMemo(() => {
    if (auth === undefined || auth?.expired === 'yes') {
      return false;
    }
    
    const hasData = !!(aiproUser?.plan_name && aiproUser?.subscription_start?.date && aiproUser?.subscription_end?.date);
    const isTrialEnd = aiproUser?.is_trial_end === 'no';
    const isNotExpired = auth?.expired !== 'yes';
    const show = subscription?.showFlashSale !== false;
    const currentPath = window.location.pathname;
    
    
      if (loading) {
        return false;
      }
      if (!isNotExpired) {
        return false;
      }
    
    if (typeof subscription?.showWhen === 'function') {
      return show && isNotExpired && subscription.showWhen({ aiproUser, loading, hasData, path: currentPath });
    }
    return show && hasData && isTrialEnd && isNotExpired;
  }, [aiproUser, loading, subscription, auth]);

  return (
    <SubscriptionProvider value={contextValue}>
      {shouldShowFlashSale ? (
        <FlashSale
          key={`${aiproUser.plan_name}|${aiproUser.subscription_start.date}|${aiproUser.subscription_end.date}`}
          aiproUser={aiproUser}
          alwaysShow={true}
          hostStart={process.env.REACT_APP_BASE_URL?.replace(/\/$/, '')}
        />
      ) : null}
      <CreditProvider>
        {children}
      </CreditProvider>
    </SubscriptionProvider>
  );
}

export default function GlobalProvider({
  children,
  queryClient,
  subscription = {},
}) {
  const qc = queryClient || new QueryClient();

  return (
    <QueryClientProvider client={qc}>
      <GlobalProviderContent subscription={subscription}>
        {children}
      </GlobalProviderContent>
    </QueryClientProvider>
  );
}


